import * as React from 'react';
import styled, { css } from 'styled-components';
import { Formik, Form, Field } from 'formik';

import DocsHelper from 'util/DocsHelper';
import { FormikInput, Icon, IconButton, Spinner } from 'components/common';
import SortableList from 'components/common/SortableList';
import { Col, DataWell, Row } from 'common/components/layout-styles';
import { Button, ControlLabel, Alert } from 'components/bootstrap';
import type ElementConfigurationContainer from 'views/components/aggregationwizard/ElementConfigurationContainer';
import { FormikMarkdownEditor } from 'security-app/components/common/FormikComponents';
import {
  useCreateEventProcedure,
  useUpdateEventProcedure,
  useGetEventProcedure,
} from 'security-app/hooks/useEventProceduresAPI';
import type {
  CreateProcedurePayloadType,
  EventProcedureStepAPIType,
  EventProceduresAPIType,
} from 'security-app/hooks/api/eventProceduresAPI.types';
import { isPermitted } from 'util/PermissionsMixin';
import useCurrentUser from 'hooks/useCurrentUser';
import EntityCreateShareFormGroup from 'components/permissions/EntityCreateShareFormGroup';

import EventProcedureStepForm from './EventProcedureStepForm';
import EventProcedureStepLibraryForm from './EventProcedureStepLibraryForm';
import EventProcedureLibraryForm from './EventProcedureLibraryForm';
import EventProcedureStepActionButton from './EventProcedureStepActionButton';

const StyledForm = styled(Form)`
  width: 100%;

  & .form-group {
    width: 100%;
  }
`;

const INIT_EVENT_PROCEDURE: CreateProcedurePayloadType = {
  title: '',
  description: '',
  steps: [],
  share_request: {},
};

const Container = styled.div(
  ({ theme }) => css`
    display: flex;
    padding: 0.5rem;
    margin: 4px 0;
    border-radius: 3px;
    border: 1px solid ${theme.colors.misc.divider};
    background-color: ${theme.colors.global.contentBackground};
    align-items: center;
    justify-content: space-between;
    position: relative;
    height: 40px;
  `,
);

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EventProcedureStepButtons = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacings.sm};
  `,
);

const StepListInfo = styled.div`
  display: flex;
  flex-direction: row;
`;

const StepListButtons = styled.div`
  display: flex;
  gap: 0.3rem;
  margin-left: 2rem;
  align-items: center;
`;

const StyledButton = styled(Button)`
  border-radius: 4px;
`;

const StyledShareSection = styled.div`
  width: 100%;
`;

type GroupingsItemProps = Omit<
  React.ComponentProps<typeof ElementConfigurationContainer>,
  'testIdPrefix' | 'onRemove' | 'elementTitle' | 'children'
> & {
  item: EventProcedureStepAPIType;
};

type Props = {
  eventProcedureID?: string | undefined;
  remediationSteps?: string;
  onClose: () => void;
  onSave?: (eventProcedureId: string) => void;
  isCreate?: boolean;
  isModalView?: boolean;
};

function EventProcedureForm({
  eventProcedureID = '',
  remediationSteps = '',
  onClose,
  onSave = () => {},
  isCreate = false,
  isModalView = false,
}: Props) {
  const [showEventProcedureForm, setShowEventProcedureForm] = React.useState(false);
  const [showStepForm, setShowStepForm] = React.useState(false);
  const [showStepLibraryForm, setShowStepLibraryForm] = React.useState(false);
  const [showLibraryForm, setShowLibraryForm] = React.useState(false);
  const [hasUpdatedStepsList, setHasUpdatedStepsList] = React.useState(false);
  const [newStepsList, setNewStepsList] = React.useState<EventProcedureStepAPIType[]>([]);
  const [viewStep, setViewStep] = React.useState<string>('');

  const { eventProcedure, loadingEventProcedure } = useGetEventProcedure(eventProcedureID);
  const { createEventProcedure, creatingEventProcedure } = useCreateEventProcedure();
  const { updateEventProcedure, updatingEventProcedure } = useUpdateEventProcedure();
  const { permissions } = useCurrentUser();
  const hasEditPermission = React.useMemo(
    () => isPermitted(permissions, `event_procedures:edit:${eventProcedureID}`),
    [eventProcedureID, permissions],
  );

  const readOnly =
    (!isCreate && !hasEditPermission) ||
    (eventProcedure?._scope && eventProcedure?._scope !== 'DEFAULT') ||
    (!eventProcedureID && !!remediationSteps);

  const preventSubmitOnEnter = (e: React.KeyboardEvent) => e.key !== 'Enter';

  const handleNewStepCreate = (step: EventProcedureStepAPIType) => {
    if (newStepsList.length > 0) {
      setNewStepsList([...newStepsList, step]);
    } else {
      const stepsList = eventProcedure?.steps?.length > 0 ? eventProcedure?.steps : [];

      setNewStepsList([...stepsList, step]);
    }
    setHasUpdatedStepsList(true);
    setShowStepForm(false);
  };

  const handleAddSteps = (steps: EventProcedureStepAPIType[]) => {
    setNewStepsList(steps);
    setHasUpdatedStepsList(true);
    setShowStepLibraryForm(false);
  };

  const removeStep = (stepsArr, stepId: string) => {
    const stepsList = stepsArr.filter((steps) => steps.id !== stepId);

    setNewStepsList(stepsList);
    setHasUpdatedStepsList(true);
  };

  const onDragList = (newList) => {
    setNewStepsList(newList);
    setHasUpdatedStepsList(true);
  };

  const handleViewClose = () => {
    setViewStep('');
  };

  const handleCancel = (resetForm: () => void) => () => {
    resetForm();
    setShowEventProcedureForm(false);
  };

  const handleDelete = () => {
    onSave('');
    onClose();
  };

  const handleAddProcedureFromLibrary = (id: string) => {
    setShowLibraryForm(false);
    onSave(id);
    onClose();
  };

  const onSubmit = (values: CreateProcedurePayloadType) => {
    const payloadObj: CreateProcedurePayloadType = {
      title: values.title,
      description: values.description,
      steps: hasUpdatedStepsList ? newStepsList : eventProcedure?.steps || [],
    };

    if (eventProcedureID) {
      updateEventProcedure({ eventProcedureId: eventProcedureID, eventProcedure: payloadObj }).then(() =>
        onSave(eventProcedureID),
      );
    } else {
      if (values.share_request?.selected_grantee_capabilities) {
        payloadObj.share_request = values.share_request;
      }
      createEventProcedure(payloadObj).then((response: EventProceduresAPIType<EventProcedureStepAPIType[]>) =>
        onSave(response.id),
      );
    }
    onClose();
  };

  const stepsListItems = React.useMemo(() => {
    if (hasUpdatedStepsList) {
      return newStepsList;
    }

    if (eventProcedure?.steps?.length > 0) {
      return eventProcedure?.steps;
    }

    return [];
  }, [eventProcedure, hasUpdatedStepsList, newStepsList]);

  const StepListItem = React.useCallback(
    ({ item, dragHandleProps, draggableProps, className, ref }: GroupingsItemProps) => (
      <Container className={className} ref={ref} {...(draggableProps ?? {})}>
        <>
          <StepListInfo>
            {dragHandleProps && (
              <DragHandle {...dragHandleProps} data-testid={`${item.id}-drag-handle`}>
                <Icon size="sm" name="drag_indicator" />
              </DragHandle>
            )}
            <div>{item.title}</div>
          </StepListInfo>
          <StepListButtons>
            <EventProcedureStepActionButton step={item} />
            <StyledButton
              bsSize="xs"
              bsStyle="gray"
              onClick={() => setViewStep(item.id)}
              aria-label={`View ${item.title}`}>
              View
            </StyledButton>
            <IconButton
              name="close"
              title="Remove"
              onClick={() => removeStep(stepsListItems, item.id)}
              aria-label={`Remove ${item.title}`}
              size="sm"
            />
          </StepListButtons>
        </>
      </Container>
    ),
    [stepsListItems],
  );

  const selectedSteps = newStepsList.length > 0 ? newStepsList : stepsListItems;

  const renderForm = () => (
    <div style={{ width: '100%' }}>
      {loadingEventProcedure ? (
        <Spinner text="Loading event procedure" />
      ) : (
        <Formik
          onSubmit={onSubmit}
          initialValues={
            eventProcedure?.title || eventProcedure?.description || eventProcedure?.steps || remediationSteps
              ? {
                  title: eventProcedure?.title,
                  description: eventProcedure?.description || remediationSteps,
                  steps: eventProcedure?.steps,
                  share_request: {},
                }
              : INIT_EVENT_PROCEDURE
          }>
          {({ isValid, dirty, submitForm, resetForm, setFieldValue }) => (
            <DataWell>
              <StyledForm id="event-procedure-form" onKeyDown={preventSubmitOnEnter}>
                <Col $gap="xl">
                  <Col $align="stretch">
                    <FormikInput
                      type="text"
                      name="title"
                      id="title"
                      label="Title"
                      validate={(value) => !value && 'Must have a title'}
                      required
                    />
                    <Field
                      id="event_procedure_description"
                      name="description"
                      component={FormikMarkdownEditor}
                      label="Description"
                      height={150}
                      disabled={readOnly}
                    />
                  </Col>
                  {!readOnly && (
                    <Col $gap="md">
                      <Row $align="center" $justify="space-between">
                        <EventProcedureStepButtons>
                          <ControlLabel>Steps</ControlLabel>
                          <StyledButton bsSize="sm" onClick={() => setShowStepForm(true)} disabled={readOnly}>
                            <Icon name="add" size="sm" />
                            New Step
                          </StyledButton>
                          <StyledButton bsSize="sm" onClick={() => setShowStepLibraryForm(true)} disabled={readOnly}>
                            <Icon name="assignment_add" size="sm" />
                            From Library
                          </StyledButton>
                        </EventProcedureStepButtons>
                        <Button
                          bsSize="sm"
                          bsStyle="link"
                          href={DocsHelper.toString(DocsHelper.PAGES.EVENT_PROCEDURES)}
                          target="_blank"
                          rel="noopener noreferrer">
                          Event Procedures Documentation
                        </Button>
                      </Row>
                      {stepsListItems.length > 0 ? (
                        <Col>
                          <SortableList<EventProcedureStepAPIType>
                            items={stepsListItems}
                            onMoveItem={(newList: EventProcedureStepAPIType[]) => onDragList(newList)}
                            customListItemRender={StepListItem}
                            fullWidth
                          />
                        </Col>
                      ) : (
                        <Alert style={{ margin: 0, width: '100%' }}>
                          Add steps to the procedure from the options above
                        </Alert>
                      )}
                    </Col>
                  )}
                  {isCreate && (
                    <StyledShareSection>
                      <EntityCreateShareFormGroup
                        description="Search for a User or Team to add as collaborator on the event procedure."
                        entityType="event_procedure"
                        onSetEntityShare={(payload) => setFieldValue('share_request', payload)}
                      />
                    </StyledShareSection>
                  )}
                  <Row $justify="flex-end" $width="100%">
                    {!isCreate && !isModalView && (
                      <Button type="reset" onClick={handleCancel(resetForm)}>
                        Cancel
                      </Button>
                    )}
                    {eventProcedureID && !isModalView && (
                      <Button bsStyle="danger" onClick={handleDelete}>
                        Remove
                      </Button>
                    )}
                    <Button
                      type="button"
                      bsStyle="success"
                      onClick={submitForm}
                      disabled={
                        (!isValid && !readOnly) ||
                        (!dirty && !hasUpdatedStepsList) ||
                        creatingEventProcedure ||
                        updatingEventProcedure
                      }>
                      {eventProcedureID ? 'Save' : 'Create'} Event Procedure
                    </Button>
                  </Row>
                </Col>
                {showStepForm && (
                  <EventProcedureStepForm onClose={() => setShowStepForm(false)} onSave={handleNewStepCreate} />
                )}
                {showStepLibraryForm && (
                  <EventProcedureStepLibraryForm
                    selectedSteps={selectedSteps}
                    onClose={() => setShowStepLibraryForm(false)}
                    onAdd={handleAddSteps}
                  />
                )}
              </StyledForm>
            </DataWell>
          )}
        </Formik>
      )}
      {viewStep && (
        <EventProcedureStepForm
          onClose={() => handleViewClose()}
          eventProcedureStep={selectedSteps.find((item) => item.id === viewStep)}
          readOnly
        />
      )}
    </div>
  );

  return (
    <div>
      {!eventProcedureID && !remediationSteps && !isCreate && (
        <>
          <ControlLabel>Event Procedure Summary</ControlLabel>
          {!showEventProcedureForm && (
            <>
              <p>There are no Event Procedures configured for this entity yet.</p>
              <Row $align="center">
                <Button onClick={() => setShowEventProcedureForm(true)} disabled={readOnly}>
                  <Icon name="add" />
                  New Event Procedure
                </Button>
                <Button onClick={() => setShowLibraryForm(true)} disabled={readOnly}>
                  <Icon name="assignment_add" />
                  From Library
                </Button>
              </Row>
              <Row style={{ marginTop: '0.3rem' }}>
                <Button onClick={onClose}>Cancel</Button>
              </Row>
            </>
          )}
        </>
      )}
      {(showEventProcedureForm || eventProcedureID || (remediationSteps && !eventProcedureID) || isCreate) &&
        renderForm()}
      {showLibraryForm && (
        <EventProcedureLibraryForm
          onClose={() => setShowLibraryForm(false)}
          onAdd={(id) => handleAddProcedureFromLibrary(id)}
        />
      )}
    </div>
  );
}

export default EventProcedureForm;
